import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import Button from '@/components/Button'
import FieldToggler from '@/components/FieldToggler'
import Icon from '@/components/Icon'
import Info from '@/components/Info'
import Pointer from '@/components/Pointer'
import Price from '@/components/Price'
import useLayout from '@/layout/useLayout'
import useTerminal from '@/lib/useTerminal'

import useCheckout from '../../hooks/useCheckout'
import Step from '../Step/Step'
import {
  ConfirmationButtons,
  ConfirmationContent,
  ConfirmationElapsed,
  ConfirmationElapsedIcon,
  ConfirmationInfo,
  ConfirmationInner,
  ConfirmationSummary
} from './Confirmation.styles'

const ConfirmationSuccess = ({ resetCheckout }: { resetCheckout: () => void }) => {
  const { t } = useTranslation()
  const { orderId, cartTotal, checkoutStartedAt } = useCheckout()
  const { accessibilityMode } = useLayout()
  const [printConfirmation, setPrintConfirmation] = useState(false)
  const [ticketsPrinted, setTicketsPrinted] = useState(false)
  const [transactionElapsedSeconds] = useState<number | null>(() => {
    if (checkoutStartedAt === null) {
      return null
    }

    return Math.max(1, Math.round((Date.now() - checkoutStartedAt) / 1000))
  })
  const { printPaymentConfirmation } = useTerminal()

  useEffect(() => {
    const previousHandler = window.printoutFinished
    const printoutFinishedHandler = () => {
      setTicketsPrinted(true)
    }

    window.printoutFinished = printoutFinishedHandler

    return () => {
      if (window.printoutFinished === printoutFinishedHandler) {
        window.printoutFinished = previousHandler
      }
    }
  }, [])

  const printReceipt = () => {
    if (!orderId) {
      toast.error(t('printError'))
      return
    }

    void printPaymentConfirmation({ orderId: String(orderId) }).catch(error => {
      toast.error(t('printError'))
      console.error('[terminal] printPaymentConfirmation error', error)
    })
  }

  const handleSubmit = () => {
    if (printConfirmation) {
      printReceipt()
    }
    resetCheckout()
  }

  return (
    <>
      <h2>{t('confirmationTitle')}</h2>
      <h3>{t('confirmationDescription')}</h3>
      {transactionElapsedSeconds !== null && (
        <ConfirmationElapsed>
          <ConfirmationElapsedIcon>
            <Icon name="clock" />
          </ConfirmationElapsedIcon>
          <span>
            <Trans i18nKey="transactionElapsedMessage" count={transactionElapsedSeconds} components={{ strong: <strong /> }} />
          </span>
        </ConfirmationElapsed>
      )}
      <ConfirmationInner>
        <ConfirmationSummary>
          <dl>
            <dt>{t('transactionNumber')}</dt>
            <dd>{orderId || '-'}</dd>
          </dl>
          <dl>
            <dt>{t('total')}</dt>
            <dd>
              <Price>{cartTotal}</Price>
            </dd>
          </dl>
          <dl>
            <dt>{t('status')}</dt>
            <dd>{t('paymentStatus.finished')}</dd>
          </dl>
          {accessibilityMode && (
            <FieldToggler defaultValue={printConfirmation} onChange={value => setPrintConfirmation(value)} label={t('iWantReceipt')} />
          )}
        </ConfirmationSummary>
        <ConfirmationInfo>
          <Info>{t('receiptQrInfo')}</Info>
        </ConfirmationInfo>
      </ConfirmationInner>
      {!accessibilityMode && (
        <FieldToggler defaultValue={printConfirmation} onChange={value => setPrintConfirmation(value)} label={t('iWantReceipt')} />
      )}
      <Button size="lg" onClick={handleSubmit}>
        {t(printConfirmation ? 'printAndFinish' : 'finish')}
      </Button>
      <Pointer isLoading={!ticketsPrinted}>{ticketsPrinted ? t('takeTickets') : t('ticketsPrinting')}</Pointer>
    </>
  )
}

const ConfirmationError = ({ resetCheckout }: { resetCheckout: () => void }) => {
  const { t } = useTranslation()
  const { paymentError, cartTotal, setCurrentStep } = useCheckout()

  const handleRetry = () => {
    setCurrentStep('PAYMENT_PENDING')
  }

  return (
    <>
      <h2>{t('checkoutErrorTitle')}</h2>
      <h3>{t('checkoutErrorDescription')}</h3>
      <ConfirmationInner>
        <ConfirmationSummary>
          <dl>
            <dt>{t('total')}</dt>
            <dd>
              <Price>{cartTotal}</Price>
            </dd>
          </dl>
          <dl>
            <dt>{t('error')}</dt>
            <dd>{paymentError}</dd>
          </dl>
        </ConfirmationSummary>
      </ConfirmationInner>
      <ConfirmationButtons>
        <Button onClick={handleRetry}>{t('tryAgain')}</Button>
        <Button onClick={resetCheckout} variant="secondary">
          {t('cancel')}
        </Button>
      </ConfirmationButtons>
    </>
  )
}

const Confirmation = () => {
  const { paymentError } = useCheckout()
  const navigate = useNavigate()
  const { reset } = useCheckout()

  const resetCheckout = () => {
    navigate('/')
    setTimeout(() => {
      reset()
    }, 500)
  }

  return (
    <Step>
      <ConfirmationContent>
        {!paymentError && <ConfirmationSuccess resetCheckout={resetCheckout} />}
        {paymentError && <ConfirmationError resetCheckout={resetCheckout} />}
      </ConfirmationContent>
    </Step>
  )
}

export default Confirmation
